/**
 * Carrier Tracking URL Configuration
 * Maps carrier names (keywords) to their online tracking URLs.
 * The {number} placeholder gets replaced with the MBL/MAWB number.
 */

const OCEAN_CARRIERS = [
  {
    keywords: ['cosco', 'oocl'],
    name: 'COSCO / OOCL',
    url: 'https://elines.coscoshipping.com/ebusiness/cargoTracking?trackingType=BOOKING&number={number}',
    stripPrefix: true,  // Remove alpha prefix (e.g. COSU6445469630 → 6445469630)
  },
  {
    keywords: ['maersk', 'sealand', 'sea-land', 'safmarine'],
    name: 'Maersk',
    url: 'https://www.maersk.com/tracking/{number}',
  },
  {
    keywords: ['msc', 'mediterranean shipping'],
    name: 'MSC',
    url: 'https://www.msc.com/en/track-a-shipment?agencyPath=usa&trackingNumber={number}',
  },
  {
    keywords: ['evergreen', 'ever '],
    name: 'Evergreen',
    url: 'https://www.shipmentlink.com/servlet/TDB1_CargoTracking.do?BkgNo={number}',
  },
  {
    keywords: ['one', 'ocean network'],
    name: 'ONE (Ocean Network Express)',
    url: 'https://ecomm.one-line.com/one-ecom/manage-shipment/cargo-tracking?trakNoParam={number}',
  },
  {
    keywords: ['yang ming', 'yangming'],
    name: 'Yang Ming',
    url: 'https://www.yangming.com/e-service/track_trace/track_trace_cargo_tracking.aspx?search={number}',
    stripPrefix: true,  // Remove alpha prefix
  },
  {
    keywords: ['hmm', 'hyundai'],
    name: 'HMM',
    url: 'https://www.hmm21.com/cms/business/ebiz/trackTrace/trackTrace/index.jsp?type=BL&number={number}',
  },
  {
    keywords: ['zim'],
    name: 'ZIM',
    url: 'https://www.zim.com/tools/track-a-shipment?consnumber={number}',
  },
  {
    keywords: ['cma', 'cgm', 'cma-cgm', 'anl', 'apl'],
    name: 'CMA CGM',
    url: 'https://www.cma-cgm.com/ebusiness/tracking/search?SearchBy=BL&Reference={number}',
  },
  {
    keywords: ['hapag', 'lloyd', 'hapag-lloyd'],
    name: 'Hapag-Lloyd',
    url: 'https://www.hapag-lloyd.com/en/online-business/track/track-by-booking-solution.html?blno={number}',
  },
  {
    keywords: ['wan hai'],
    name: 'Wan Hai',
    url: 'https://www.wanhai.com/views/cargoTrack/CargoTrack.xhtml?bookingNum={number}',
  },
  {
    keywords: ['pil', 'pacific international'],
    name: 'PIL',
    url: 'https://www.pilship.com/en--/120.html?BlNo={number}',
  },
  {
    keywords: ['matson'],
    name: 'Matson',
    url: 'https://www.matson.com/shipment-tracking.html?tracking={number}',
  },
];

const AIR_CARRIERS = [
  {
    keywords: ['fedex', 'federal express'],
    name: 'FedEx',
    url: 'https://www.fedex.com/fedextrack/?trknbr={number}',
  },
  {
    keywords: ['ups', 'united parcel'],
    name: 'UPS',
    url: 'https://www.ups.com/track?tracknum={number}',
  },
  {
    keywords: ['dhl'],
    name: 'DHL',
    url: 'https://www.dhl.com/us-en/home/tracking/tracking-express.html?submit=1&tracking-id={number}',
  },
];

// General fallback tracking sites
const FALLBACK_OCEAN = {
  name: 'Track-Trace.com',
  url: 'https://www.track-trace.com/container?number={number}',
};

const FALLBACK_AIR = {
  name: 'FlightAware',
  url: 'https://www.flightaware.com/live/flight/{number}',
};

/**
 * Find the best tracking URL for a given carrier/vessel name and tracking number.
 * @param {string} carrierOrVessel - Carrier name, vessel name, or any identifying text
 * @param {string} trackingNumber - MBL, HBL, MAWB, or HAWB number
 * @param {string} shipmentType - 'ocean-import', 'ocean-export', 'air-import', 'truck', etc.
 * @returns {{ name: string, url: string } | null}
 */
export function getTrackingUrl(carrierOrVessel, trackingNumber, shipmentType = '') {
  if (!trackingNumber) return null;

  const searchText = (carrierOrVessel || '').toLowerCase();
  const isAir = shipmentType.toLowerCase().includes('air');

  // Search appropriate carrier list
  const carriers = isAir ? AIR_CARRIERS : OCEAN_CARRIERS;

  for (const carrier of carriers) {
    for (const keyword of carrier.keywords) {
      if (searchText.includes(keyword.toLowerCase())) {
        // Strip alpha prefix if carrier requires it (e.g. COSU6445469630 → 6445469630)
        let num = trackingNumber;
        if (carrier.stripPrefix) {
          num = trackingNumber.replace(/^[A-Za-z]+/, '');
        }
        return {
          name: carrier.name,
          url: carrier.url.replace('{number}', encodeURIComponent(num)),
        };
      }
    }
  }

  // Fallback to general tracking
  const fallback = isAir ? FALLBACK_AIR : FALLBACK_OCEAN;
  return {
    name: fallback.name,
    url: fallback.url.replace('{number}', encodeURIComponent(trackingNumber)),
  };
}

/**
 * Get all available carrier tracking options for a given number.
 * Used when carrier can't be auto-detected (shows a picker).
 */
export function getAllCarriers(shipmentType = '') {
  const isAir = shipmentType.toLowerCase().includes('air');
  return isAir ? AIR_CARRIERS : OCEAN_CARRIERS;
}

export default { getTrackingUrl, getAllCarriers };
